import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';


const HomeHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationLinks = [
    { label: 'Features', icon: 'Sparkles', to: '/features' },
    { label: 'How It Works', icon: 'Workflow', href: '/#how-it-works' },
    { label: 'Pricing', icon: 'Tag', to: '/pricing' },
    { label: 'Templates', icon: 'LayoutTemplate', to: '/templates-gallery' },
    { label: 'About', icon: 'Info', to: '/about' },
  ];

  const scrollToSection = (e, href) => {
    const hash = href?.includes('#') ? `#${href.split('#')[1]}` : null;
    if ((pathname === '/' || pathname === '/home-page') && hash) {
      const element = document.querySelector(hash);
      if (element) {
        e?.preventDefault();
        const offset = 80;
        const elementPosition = element?.getBoundingClientRect()?.top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        setMobileMenuOpen(false);
      }
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-white/95 backdrop-blur-xl shadow-sm border-b border-gray-100' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center transition-opacity hover:opacity-80">
            <div className="w-36 sm:w-44 lg:w-48 h-auto">
              <Image
                src="/assets/images/web-forms-logo-1769800970615.png"
                alt="WebForms.biz - Professional Form Builder"
                className="w-full h-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navigationLinks?.map((link) => (
              link?.to ? (
                <Link
                  key={link?.label}
                  to={link?.to}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Icon name={link?.icon} size={15} color="#097770" aria-hidden="true" />
                  {link?.label}
                </Link>
              ) : (
                <a
                  key={link?.label}
                  href={link?.href}
                  onClick={(e) => scrollToSection(e, link?.href)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
                >
                  <Icon name={link?.icon} size={15} color="#097770" aria-hidden="true" />
                  {link?.label}
                </a>
              )
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            <Link
              to="/authentication"
              className="hidden lg:block text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-3 py-2"
            >
              Log in
            </Link>
            <Link
              to="/authentication"
              className="hidden lg:flex items-center gap-2 px-5 py-2.5 bg-[#0e7572] hover:bg-[#0a5f5c] text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <span>Start for free</span>
              <Icon name="ArrowRight" size={15} />
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle mobile menu"
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} className="text-gray-700" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white/98 backdrop-blur-lg">
          <div className="px-4 py-6 space-y-4">
            <nav className="space-y-1">
              {navigationLinks?.map((link) => (
                link?.to ? (
                  <Link
                    key={link?.label}
                    to={link?.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-3 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-colors"
                  >
                    <Icon name={link?.icon} size={18} color="#097770" aria-hidden="true" />
                    {link?.label}
                  </Link>
                ) : (
                  <a
                    key={link?.label}
                    href={link?.href}
                    onClick={(e) => scrollToSection(e, link?.href)}
                    className="flex items-center gap-2 px-4 py-3 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer"
                  >
                    <Icon name={link?.icon} size={18} color="#097770" aria-hidden="true" />
                    {link?.label}
                  </a>
                )
              ))}
            </nav>
            <div className="pt-4 border-t border-gray-100 space-y-3">
              <Link
                to="/authentication"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-center px-4 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:border-gray-300 transition-colors"
              >
                Log in
              </Link>
              <Link
                to="/authentication"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-[#0e7572] text-white font-semibold rounded-xl hover:bg-[#0a5f5c] transition-colors"
              >
                <span>Start for free</span>
                <Icon name="ArrowRight" size={16} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default HomeHeader;